import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { CustomRouterNavigatedAction } from '../../../core/custom-route-serializer';
import { STUDIES_ROUTES } from '../../studies/studies.routes';
import * as metricsActions from '../metrics/metrics.actions';
import * as studyActions from '../study/study.actions';
import { MetricsService } from '../../services/metrics/metrics.service';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';

@Injectable()
export class MetricsEffects {
  metricsRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) => {
        return route.payload.routerState.url.endsWith(`/${STUDIES_ROUTES.METRICS}`);
      }),
      switchMap((action) => [
        metricsActions.getFilters({ studyId: action.payload.routerState.params.id }),
        studyActions.selectStudy({ studyId: action.payload.routerState.params.id }),
      ])
    )
  );

  getMetrics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.getMetrics),
      switchMap(({ studyId, interventionId, moduleId }) => {
        return this.metricsService.getResponsesForMetrics(studyId, interventionId, moduleId).pipe(
          map((responsesForMetrics) =>
            metricsActions.getMetricsSuccess({
              metrics: this.dashboardCamelCasePipe.keysToCamelCase(responsesForMetrics),
            })
          ),
          catchError((err: HttpErrorResponse) => of(metricsActions.getMetricsError({ err })))
        );
      })
    )
  );

  getFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.getFilters),
      switchMap(({ studyId }) => {
        return this.metricsService.getFilterValues(studyId).pipe(
          map((filter) =>
            metricsActions.getFiltersSuccess({
              filter: filter,
            })
          ),
          catchError((err: HttpErrorResponse) => of(metricsActions.getMetricsError({ err })))
        );
      })
    )
  );

  getUnansweredQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.getUnansweredQuestions),
      switchMap(({ studyId, interventionId, moduleId }) => {
        return this.metricsService.getUnansweredQuestionsForMetrics(studyId, interventionId, moduleId).pipe(
          map((unanswered) =>
            metricsActions.getUnansweredQuestionsSuccess({
              unanswered: this.dashboardCamelCasePipe.keysToCamelCase(unanswered),
            })
          ),
          catchError((err: HttpErrorResponse) => of(metricsActions.getUnansweredQuestionsError({ err })))
        );
      })
    )
  );

  getParticipantScreeningResponses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.getParticipantScreeningResponses),
      switchMap(({ studyId, participantId }) => {
        return this.metricsService.getParticipantResponsesForScreening(studyId, participantId).pipe(
          map((responsesForScreening) =>
            metricsActions.getParticipantScreeningResponsesSuccess({
              metrics: this.dashboardCamelCasePipe.keysToCamelCase(responsesForScreening),
            })
          ),
          catchError((err: HttpErrorResponse) => of(metricsActions.getParticipantScreeningResponsesError({ err })))
        );
      })
    )
  );

  downloadInterventionAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.downloadInterventionAnswers),
      switchMap(({ languageCode, interventionId, version}) => {
        return this.metricsService.downloadInterventionAnswers(languageCode, interventionId, version).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `Export-metrics-intervention-${interventionId}.csv`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'text/csv' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return metricsActions.downloadInterventionAnswersSuccess();
          }),
          catchError((err: HttpErrorResponse) => of(metricsActions.downloadInterventionAnswersError({ err })))
        );
      })
    )
  );

  downloadInterventionAnswersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(metricsActions.downloadInterventionAnswersError),
        tap(({ err }) => this.toastrService.error('Unable to download answers'))
      ),
    { dispatch: false }
  );

  downloadInterventionAnswersTiming$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.downloadInterventionAnswersTiming),
      switchMap(({ interventionId }) => {
        return this.metricsService.downloadInterventionAnswersTiming(interventionId).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `Summary-report${interventionId}.csv`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'text/csv' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return metricsActions.downloadInterventionAnswersTimingSuccess();
          }),
          catchError((err: HttpErrorResponse) => of(metricsActions.downloadInterventionAnswersTimingError({ err })))
        );
      })
    )
  );

  downloadInterventionAnswersTimingError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(metricsActions.downloadInterventionAnswersTimingError),
        tap(({ err }) => this.toastrService.error('Unable to download summary report'))
      ),
    { dispatch: false }
  );

  downloadInterventionFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.downloadInterventionFiles),
      switchMap(({ keyForFiles, studyId, interventionId, moduleId }) => {
        return this.metricsService.downloadInterventionFiles(keyForFiles, studyId, interventionId, moduleId).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `moduleId-${moduleId}.zip`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'application/zip' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return metricsActions.downloadInterventionFilesSuccess();
          }),
          catchError((err: HttpErrorResponse) => of(metricsActions.downloadInterventionFilesError({ err })))
        );
      })
    )
  );

  downloadInterventionFilesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(metricsActions.downloadInterventionFilesError),
        tap(({ err }) => {
          let message = 'Unable to download files';
          if (typeof err.error === 'object') {
            const enc = new TextDecoder("utf-8");
            const error = enc.decode(err.error);
            message = JSON.parse(error).message;
          }
          this.toastrService.error(message);
        })
      ),
    { dispatch: false }
  );

  downloadInterventionParticipantData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(metricsActions.downloadInterventionParticipantData),
      switchMap(({ interventionId, participantId }) => {
        return this.metricsService.downloadInterventionParticipantData(interventionId, participantId).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `InterventionId-${interventionId}-participantId-${participantId}.pdf`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'application/pdf' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return metricsActions.downloadInterventionParticipantDataSuccess();
          }),
          catchError((err: HttpErrorResponse) => of(metricsActions.downloadInterventionParticipantDataError({ err })))
        );
      })
    )
  );

  downloadInterventionParticipantDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(metricsActions.downloadInterventionParticipantDataError),
        tap(({ err }) => this.toastrService.error('Unable to download participant data'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private metricsService: MetricsService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private toastrService: ToastrService
  ) {}
}
